import { useEffect, useMemo, useRef, useState } from "react";

import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, Phone, User, X } from "lucide-react";

import { Button as MuiButton } from "@mui/material";

import {
  getCarUrl,
  isCarHost,
  TAILWIND_BUTTON_BASE_CLASS,
  TAILWIND_SUCCESS_BUTTON_RING_CLASS,
} from "../helpers/constants";
import AlertDialog from "../common/AlertDialog";
import { Button } from "../common/customDesignedComponents/button";
import epLogo from "../../assets/new_ui_images/equity-protect-logo.png";
import {
  clearStaleAuthSession,
  decryptPayload,
  encryptPayload,
  exitSignupToLogin,
  findAllowedAttorneyPrefix,
  getSignupResumeStep,
  getNativeAppSafePath,
  getObjLength,
  isAuthenticatedSession,
  isNativeAppShell,
  shouldHideMyAccountButton,
  shouldResumeSignupFromMyAccount,
  navigateToCustomerDomain,
  navigateToDomain,
  navigateToSignupResume,
  redirectToLogin,
} from "../helpers/utils";
import {
  myAccountOpensOnServicerSite,
  resolveMyAccountDestination,
  shouldPromptIncompleteSignup,
} from "../helpers/myAccountNavigation";
import { isServiceProviderPortalHost } from "../auth/postLoginNavigation";

import "../../tailwind.css";

const isAbsoluteHttpHref = (href: string) => /^https?:\/\//i.test(href);

const CAR_MEMBERS_MENU_NAME = "California Association of REALTORS Members";

const UCC_REQUEST_MENU_NAME = "Request a UCC Waiver or Termination";

/**
 * On the CAR site the For Service Providers items leave it: the UCC request
 * opens the service-provider site, the partnership pages the EquityProtect site.
 */
export const openCarSiteSubmenuLink = (subLink: { name: string; href: string }) => {
  if (subLink.name === UCC_REQUEST_MENU_NAME) {
    navigateToDomain("for-service-provider");
    return;
  }
  navigateToCustomerDomain(subLink.href);
};

type NavLink = {
  name: string;
  href: string;
  isServiceProvider?: boolean;
  isInvestorPage?: boolean;
  allow_county_code?: boolean;
  submenu?: { name: string; href: string }[];
};

/**
 * Links the navbar shows. Service-provider mode keeps Home + For Service
 * Providers; on the CAR site the CAR Members item is dropped because it would
 * point back at the page the visitor is on.
 */
export const filterHeaderNavLinks = (
  links: NavLink[],
  {
    isServiceProvider = false,
    isInvestorPage = false,
    excludeLinks = [],
    onCarHost = false,
    nativeAppShell = false,
  }: {
    isServiceProvider?: boolean;
    isInvestorPage?: boolean;
    excludeLinks?: string[];
    onCarHost?: boolean;
    nativeAppShell?: boolean;
  } = {},
): NavLink[] => {
  let result = isServiceProvider
    ? links.filter((link) => link.isServiceProvider === true)
    : isInvestorPage
      ? links.filter((link) => link.isInvestorPage === true)
      : links;
  if (excludeLinks.length > 0) {
    result = result.filter((link) => !excludeLinks.includes(link.name));
  }
  // Marketing Home has no place in the native app shell.
  if (nativeAppShell) {
    result = result.filter((link) => link.name !== "Home");
  }
  if (onCarHost) {
    result = result.map((link) =>
      link.submenu
        ? {
            ...link,
            submenu: link.submenu.filter(
              (subLink) => subLink.name !== CAR_MEMBERS_MENU_NAME,
            ),
          }
        : link,
    );
  }
  return result;
};

const navLinks: NavLink[] = [
  {
    name: "Home",
    href: "/",
    isServiceProvider: true,
    allow_county_code: true,
    isInvestorPage: true,
  },
  {
    name: "How We Help",
    href: "/how-it-works",
    isInvestorPage: true,
    submenu: [
      { name: "How Equity Protect Works", href: "/how-it-works" },
      {
        name: "Property Risk Assessment",
        href: "/property-risk-management",
      },
      {
        name: "Reactive Notifications",
        href: "/home-title-monitoring-notifications",
      },
      {
        name: "Proactive Title Lock",
        href: "/title-lock-protection-for-your-home",
      },
    ],
  },
  { name: "Pricing", href: "/pricing", allow_county_code: true },
  {
    name: "Home Title Theft",
    href: "/what-is-home-title-theft",
    isInvestorPage: true,
    submenu: [
      { name: "What is Title Theft", href: "/what-is-home-title-theft" },
      {
        name: "Risk for New Homeowners",
        // href: "/title-protection-long-time-homeowners",
        href: "/home-title-theft-risk-new-homeowners",
      },
      {
        name: "Risk for Real Estate Investors",
        // href: "/title-protection-real-estate-investors",
        href: "/title-theft-protection-real-estate-investors",
      },
      {
        name: "Risk for Legacy Owners",
        // href: "/title-protection-real-estate-investors",
        href: "/home-title-protection-long-time-homeowners",
      },
      // {
      //   name: "Compare Protection Options",
      //   href: "/why-monitoring-isnt-enough",
      // },
    ],
  },
  {
    name: "Legislative Map",
    href: "/scorecard",
  },
  {
    name: "For Service Providers",
    href: "/for-service-provider",
    isServiceProvider: true,
    submenu: [
      {
        name: "Real Estate Professional Partnerships",
        href: "/real-estate-partnerships",
      },
      {
        name: "County Recorder Partnerships",
        href: "/county-recorder-partnerships",
      },
      {
        name: UCC_REQUEST_MENU_NAME,
        href: "/for-service-provider",
      },
      {
        name: CAR_MEMBERS_MENU_NAME,
        href: getCarUrl(),
      },
    ],
  },
];

export const Header = ({
  isServiceProvider = false,
  isInvestorPage = false,
  isLoginPage = false,
  excludeLinks = [],
  hideAccountBtn = false,
  phoneNumber = "(888) 213-2959",
  phoneNumberHref = "tel:8882132959",
  prefixNavLinks = false,
  prefixNotForHowItWorksLink = false,
  logoTagline = "Our Technology Your Control",
  homeHref,
}: {
  isServiceProvider?: boolean;
  isInvestorPage?: boolean;
  isLoginPage?: boolean;
  excludeLinks?: string[];
  hideAccountBtn?: boolean;
  phoneNumber?: string;
  phoneNumberHref?: string;
  prefixNavLinks?: boolean;
  prefixNotForHowItWorksLink?: boolean;
  logoTagline?: string;
  homeHref?: string;
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // "You've left signup incomplete" prompt (public pages, My Account).
  const [showIncompleteSignupDialog, setShowIncompleteSignupDialog] =
    useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState<string | null>(
    null,
  );
  const submenuRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const navigate = useNavigate();
  const pathname = window.location.pathname;

  const homeLink = navLinks.find((l) => l.name === "Home");
  const allowCountyCode = homeLink?.allow_county_code;

  const queryString = window.location.search;
  let paramString = queryString.split("?")[1];
  let valueOf = paramString?.split("=")[0];

  let is_scrp_hp = localStorage.getItem("scrp_hp");
  let countyCode = decryptPayload(localStorage.getItem("county-code"));

  // When prefixNavLinks is true, read the :countyAllowed param from the current route
  // and prepend it to nav link hrefs (only for actual county codes, not attorney/industrypartner)
  const { countyAllowed, countyin } = useParams<{
    countyAllowed?: string;
    countyin?: string;
  }>();
  const prefixMatch =
    prefixNavLinks && (countyAllowed || countyin)
      ? findAllowedAttorneyPrefix(countyAllowed || countyin)
      : null;
  const activePrefix =
    prefixMatch && (prefixMatch as { isCounty?: boolean }).isCounty
      ? `/${countyAllowed || countyin}`
      : "";

  const prefixHref = (href: string): string =>
    activePrefix
      ? href === "/"
        ? activePrefix
        : `${activePrefix}${href}`
      : href;

  const getNavHref = (href: string, parentLinkName?: string) =>
    prefixNotForHowItWorksLink && parentLinkName === "How It Works"
      ? href
      : prefixHref(href);

  // store data
  const { riskManagementData } = useSelector(
    (state: any) => state?.propertyRiskManagement,
  );

  const maangeCountyData = (county_details) => {
    if (getObjLength(county_details)) {
      is_scrp_hp = "1";
      countyCode = encryptPayload(county_details?.county_domain_name);
    }
  };

  const shouldHideAccountBtn = hideAccountBtn || shouldHideMyAccountButton();

  // handle My Account Button
  // Never logs the visitor in from leftover signup data (stored signup token
  // or saved signup credentials): an unauthenticated visitor always goes to
  // the login page, which routes pending customers to Resume Signup itself.
  const handleMyAccountBtn = () => {
    void (async () => {
      clearStaleAuthSession();

      if (shouldResumeSignupFromMyAccount()) {
        navigateToSignupResume(navigate);
        localStorage.removeItem("param");
        localStorage.removeItem("fixParams");
        localStorage.removeItem("searched_Add");
        localStorage.removeItem("searched_Add_popup");
        return;
      }

      const isAuthenticated = isAuthenticatedSession();

      // Public page + resumable signup in this browser: let the visitor choose
      // between finishing signup and going to login (which clears local data).
      if (
        !isServiceProvider &&
        shouldPromptIncompleteSignup({
          isAuthenticated,
          resumeStep: getSignupResumeStep(),
        })
      ) {
        setIsMenuOpen(false);
        setShowIncompleteSignupDialog(true);
        return;
      }

      const destination = resolveMyAccountDestination({
        isAuthenticated,
        roles: decryptPayload(localStorage.getItem("roles")),
        isServiceProvider,
      });

      if (!destination) {
        redirectToLogin(navigate);
      } else if (
        myAccountOpensOnServicerSite({
          destination,
          onServicerHost: isServiceProviderPortalHost(),
        })
      ) {
        navigateToDomain(destination);
      } else {
        navigate(destination);
      }

      localStorage.removeItem("param");
      localStorage.removeItem("fixParams");
      localStorage.removeItem("searched_Add");
      localStorage.removeItem("searched_Add_popup");
    })();
  };

  const onCarSite = isCarHost();

  // When isServiceProvider is true, show only links with isServiceProvider: true; otherwise show all
  const filteredNavLinks = useMemo(
    () =>
      filterHeaderNavLinks(navLinks, {
        isServiceProvider,
        isInvestorPage,
        excludeLinks,
        onCarHost: isCarHost(),
        nativeAppShell: isNativeAppShell(),
      }),
    [isServiceProvider, isInvestorPage, excludeLinks],
  );

  // Home follows homeHref (the CAR landing page on the CAR site); without it,
  // service-provider Home goes to the customer site.
  const navLinkHref = (link: NavLink) =>
    link.name === "Home" && homeHref ? homeHref : prefixHref(link.href);
  const homeOpensCustomerSite = (link: NavLink) =>
    isServiceProvider && link.name === "Home" && !homeHref;
  // homeHref can be another site (the CAR landing page from servicer login);
  // router links only handle paths on this site.
  const homeIsOtherSite = /^https?:\/\//i.test(homeHref || "");
  const openOtherSiteHome = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    window.location.href = homeHref as string;
  };

  // Close submenu when clicking outside any open submenu panel
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedInsideSubmenu = Object.values(submenuRefs.current).some(
        (submenuEl) => submenuEl && submenuEl.contains(event.target as Node),
      );

      if (!clickedInsideSubmenu) {
        setOpenSubmenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (pathname?.includes("property-risk-report")) {
      document.getElementById("HeaderNavbar")?.classList.add("sticky");
      if (
        valueOf === "report_token" &&
        getObjLength(riskManagementData) &&
        riskManagementData?.success
      ) {
        let county_details = riskManagementData?.data?.county;
        maangeCountyData(county_details);
        // set county if it's already available
        if (getObjLength(county_details)) {
          localStorage.setItem("scrp_hp", "1");
          localStorage.setItem(
            "county-code",
            encryptPayload(county_details?.county_domain_name),
          );
        } else {
          localStorage.removeItem("scrp_hp");
          localStorage.removeItem("county-code");
        }
      }
    }
  }, [pathname]);

  const clearSearchedPropertyKeys = () => {
    localStorage.removeItem("param");
    localStorage.removeItem("fixParams");
    localStorage.removeItem("searched_Add");
    localStorage.removeItem("searched_Add_popup");
  };

  const continueIncompleteSignup = () => {
    setShowIncompleteSignupDialog(false);
    clearSearchedPropertyKeys();
    // autoResumeSignup: the wizard skips its own "resume?" prompt and resumes.
    navigateToSignupResume(navigate, { state: { autoResumeSignup: true } });
  };

  const leaveIncompleteSignupForLogin = () => {
    setShowIncompleteSignupDialog(false);
    clearSearchedPropertyKeys();
    // Clears every local signup value (step, user id, credentials, stored
    // signup token) and opens /login. The account itself stays pending on the
    // server; logging in later still resumes the signup.
    exitSignupToLogin(navigate);
  };

  return (
    <header
      className={`top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-md border-b border-border tailwind_component ${!isLoginPage ? "fixed" : ""}`}
    >
      {showIncompleteSignupDialog && (
        <AlertDialog
          isTailwindComponent
          openPopup
          onClose={false}
          type="Warning"
          mesage="Your signup isn't finished yet. Continue where you left off, or go to the login page? Going to login clears the signup data saved in this browser."
          buttons={
            <>
              <MuiButton
                className={`m-2 ${TAILWIND_BUTTON_BASE_CLASS}`}
                onClick={leaveIncompleteSignupForLogin}
              >
                GO TO LOGIN
              </MuiButton>
              <MuiButton
                className={`m-2 ${TAILWIND_SUCCESS_BUTTON_RING_CLASS}`}
                onClick={continueIncompleteSignup}
              >
                Continue signup
              </MuiButton>
            </>
          }
        />
      )}
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link
            to={homeHref || prefixHref("/")}
            className="flex flex-col items-start"
            onClick={(e) => {
              // Native app shell must never open the marketing homepage.
              if (isNativeAppShell()) {
                e.preventDefault();
                const safePath = getNativeAppSafePath();
                if (safePath !== pathname) {
                  navigate(safePath, { replace: true });
                }
                return;
              }
              if (homeIsOtherSite) {
                openOtherSiteHome(e);
                return;
              }
              if (homeHref) {
                return;
              }
              if (isServiceProvider) {
                e.preventDefault();
                navigateToCustomerDomain("");
                return;
              }
              if (parseInt(is_scrp_hp) === 1 && countyCode && allowCountyCode) {
                e.preventDefault();
                navigate(`/county-recorder/${countyCode}`);
              }
            }}
          >
            <img
              src={epLogo}
              alt="EquityProtect"
              className="h-10 md:h-12 w-auto"
            />
            {logoTagline && (
              <span className="text-[11px] md:text-xs font-medium text-primary tracking-wide mt-0.5">
                {logoTagline}
              </span>
            )}
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden min-[1200px]:flex items-center gap-4 xl:gap-6 2xl:gap-8 flex-shrink-0 px-2">
            {isLoginPage
              ? null
              : filteredNavLinks.map((link) =>
                  link.submenu ? (
                    <div
                      key={link.name}
                      className="relative"
                      ref={(el) => {
                        submenuRefs.current[link.name] = el;
                      }}
                      onMouseEnter={() => setOpenSubmenu(link.name)}
                      onMouseLeave={() => setOpenSubmenu(null)}
                    >
                      <button
                        className="flex items-center gap-1 text-sm font-medium text-foreground/80 hover:text-secondary transition-colors whitespace-nowrap"
                        onClick={() =>
                          setOpenSubmenu(
                            openSubmenu === link.name ? null : link.name,
                          )
                        }
                      >
                        {link.name}
                        <ChevronDown
                          className={`h-4 w-4 transition-transform ${
                            openSubmenu === link.name ? "rotate-180" : ""
                          }`}
                        />
                      </button>

                      <AnimatePresence>
                        {openSubmenu === link.name && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            transition={{ duration: 0.15 }}
                            className="absolute top-full left-0 pt-2 w-80 z-50"
                          >
                            <div className="bg-card rounded-lg shadow-xl border border-border overflow-hidden">
                              {link.submenu.map((subLink) =>
                                isAbsoluteHttpHref(subLink.href) ? (
                                  <a
                                    key={subLink.name}
                                    href={subLink.href}
                                    className="block px-4 py-3 text-sm text-foreground hover:bg-muted hover:text-secondary transition-colors"
                                    onClick={() => setOpenSubmenu(null)}
                                  >
                                    {subLink.name}
                                  </a>
                                ) : (
                                  <Link
                                    key={subLink.name}
                                    to={getNavHref(subLink.href, link.name)}
                                    className="block px-4 py-3 text-sm text-foreground hover:bg-muted hover:text-secondary transition-colors"
                                    onClick={(e) => {
                                      if (onCarSite) {
                                        e.preventDefault();
                                        setOpenSubmenu(null);
                                        openCarSiteSubmenuLink(subLink);
                                        return;
                                      }
                                      if (
                                        parseInt(is_scrp_hp) === 1 &&
                                        countyCode &&
                                        link.allow_county_code
                                      ) {
                                        e.preventDefault();
                                        navigate(
                                          subLink.href === "/"
                                            ? `/county-recorder/${countyCode}`
                                            : `/${countyCode}${subLink.href}`,
                                        );
                                      }
                                      setOpenSubmenu(null);
                                    }}
                                  >
                                    {subLink.name}
                                  </Link>
                                ),
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : link.href.startsWith("/") && !link.href.includes("#") ? (
                    <Link
                      key={link.name}
                      to={navLinkHref(link)}
                      className="text-sm font-medium text-foreground/80 hover:text-secondary transition-colors whitespace-nowrap"
                      onClick={(e) => {
                        if (link.name === "Home" && homeIsOtherSite) {
                          openOtherSiteHome(e);
                          return;
                        }
                        if (homeOpensCustomerSite(link)) {
                          e.preventDefault();
                          navigateToCustomerDomain("");
                          return;
                        }
                        if (
                          !isServiceProvider &&
                          link.name === "Request a UCC Waiver or Termination"
                        ) {
                          e.preventDefault();
                          navigateToDomain("for-service-provider");
                          return;
                        }
                        if (
                          parseInt(is_scrp_hp) === 1 &&
                          countyCode &&
                          link.allow_county_code
                        ) {
                          e.preventDefault();
                          navigate(
                            link.href === "/"
                              ? `/county-recorder/${countyCode}`
                              : `/${countyCode}${link.href}`,
                          );
                        }
                      }}
                    >
                      {link.name}
                    </Link>
                  ) : (
                    <a
                      key={link.name}
                      href={link.href}
                      className="text-sm font-medium text-foreground/80 hover:text-secondary transition-colors whitespace-nowrap"
                    >
                      {link.name}
                    </a>
                  ),
                )}
          </nav>

          {/* Desktop CTA */}
          {isLoginPage ? null : (
            <div className={`hidden min-[1200px]:flex items-center gap-4`}>
              <a href={phoneNumberHref}>
                <Button variant="outline" size="sm" className="gap-2">
                  <Phone className="h-4 w-4" />
                  <span className="hidden min-[1400px]:inline">
                    {phoneNumber}
                  </span>
                </Button>
              </a>
              {!shouldHideAccountBtn && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleMyAccountBtn}
                >
                  <User className="h-4 w-4" />
                  <span className="hidden  min-[1400px]:inline">
                    My Account
                  </span>
                </Button>
              )}
            </div>
          )}

          {/* Mobile Menu Button */}
          {!isLoginPage && !hideAccountBtn && (
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="min-[1200px]:hidden p-2 text-foreground"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="min-[1200px]:hidden bg-card border-b border-border overflow-hidden"
          >
            <nav className="container mx-auto px-4 py-4 flex flex-col gap-2">
              {isLoginPage
                ? null
                : filteredNavLinks.map((link) =>
                    link.submenu ? (
                      <div key={link.name}>
                        <button
                          className="flex items-center justify-between w-full text-sm font-medium text-foreground/80 hover:text-secondary py-2"
                          onClick={() =>
                            setMobileSubmenuOpen(
                              mobileSubmenuOpen === link.name
                                ? null
                                : link.name,
                            )
                          }
                        >
                          {link.name}
                          <ChevronDown
                            className={`h-4 w-4 transition-transform ${
                              mobileSubmenuOpen === link.name
                                ? "rotate-180"
                                : ""
                            }`}
                          />
                        </button>
                        <AnimatePresence>
                          {mobileSubmenuOpen === link.name && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              className="pl-4 overflow-hidden"
                            >
                              {link.submenu.map((subLink) =>
                                isAbsoluteHttpHref(subLink.href) ? (
                                  <a
                                    key={subLink.name}
                                    href={subLink.href}
                                    className="block text-sm text-foreground/70 hover:text-secondary py-2"
                                    onClick={() => {
                                      setIsMenuOpen(false);
                                      setMobileSubmenuOpen(null);
                                    }}
                                  >
                                    {subLink.name}
                                  </a>
                                ) : (
                                  <Link
                                    key={subLink.name}
                                    to={getNavHref(subLink.href, link.name)}
                                    className="block text-sm text-foreground/70 hover:text-secondary py-2"
                                    onClick={(e) => {
                                      if (onCarSite) {
                                        e.preventDefault();
                                        setIsMenuOpen(false);
                                        setMobileSubmenuOpen(null);
                                        openCarSiteSubmenuLink(subLink);
                                        return;
                                      }
                                      if (
                                        parseInt(is_scrp_hp) === 1 &&
                                        countyCode &&
                                        link.allow_county_code
                                      ) {
                                        e.preventDefault();
                                        navigate(
                                          subLink.href === "/"
                                            ? `/county-recorder/${countyCode}`
                                            : `/${countyCode}${subLink.href}`,
                                        );
                                      }
                                      setIsMenuOpen(false);
                                      setMobileSubmenuOpen(null);
                                    }}
                                  >
                                    {subLink.name}
                                  </Link>
                                ),
                              )}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : link.href.startsWith("/") &&
                      !link.href.includes("#") ? (
                      <Link
                        key={link.name}
                        to={navLinkHref(link)}
                        className="text-sm font-medium text-foreground/80 hover:text-secondary py-2"
                        onClick={(e) => {
                          if (link.name === "Home" && homeIsOtherSite) {
                            openOtherSiteHome(e);
                            return;
                          }
                          if (homeOpensCustomerSite(link)) {
                            e.preventDefault();
                            navigateToCustomerDomain("");
                            setIsMenuOpen(false);
                            return;
                          }
                          if (
                            !isServiceProvider &&
                            link.name === "Request a UCC Waiver or Termination"
                          ) {
                            e.preventDefault();
                            navigateToDomain("for-service-provider");
                            setIsMenuOpen(false);
                            return;
                          }
                          if (
                            parseInt(is_scrp_hp) === 1 &&
                            countyCode &&
                            link.allow_county_code
                          ) {
                            e.preventDefault();
                            navigate(
                              link.href === "/"
                                ? `/county-recorder/${countyCode}`
                                : `/${countyCode}${link.href}`,
                            );
                          }
                          setIsMenuOpen(false);
                        }}
                      >
                        {link.name}
                      </Link>
                    ) : (
                      <a
                        key={link.name}
                        href={link.href}
                        className="text-sm font-medium text-foreground/80 hover:text-secondary py-2"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {link.name}
                      </a>
                    ),
                  )}
              <div className="flex flex-col gap-3 pt-4 border-t border-border">
                <a href={phoneNumberHref}>
                  <Button variant="outline" className="w-full gap-2">
                    <Phone className="h-4 w-4" />
                    {phoneNumber}
                  </Button>
                </a>
                {isLoginPage || shouldHideAccountBtn ? null : (
                  <Button
                    variant="secondary"
                    className="w-full"
                    onClick={() => {
                      setIsMenuOpen(false);
                      handleMyAccountBtn();
                    }}
                  >
                    My Account
                  </Button>
                )}
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
